import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  addOfflineCenterFee,
  deleteCenterFeePayment,
  getCenterFeeDetails,
  getCenterFeeList,
  getCenterFeeSetting,
  saveCenterFeeSetting,
  setCenterRenewalDate,
  FILE_BASE,
} from "../../../store/actions/councilActions/centerFeeActions";
import { errorAlert } from "../../../utils/alert";
import Paging from "../../Common/Paging";

/*
 * Admin: center registration fee and yearly renewal.
 *
 * Every amount is typed here by hand. The partial amount is the smallest first
 * instalment a center may pay - it is not a percentage of the full fee.
 * Renewal is counted per renewal year, so clearing last year's renewal never
 * settles this year's.
 */
const rupees = (value) => "₹" + Number(value || 0).toLocaleString("en-IN");

const STATE_LABEL = {
  paid: "Fully paid",
  partial: "Part paid",
  unpaid: "Not paid",
};

const dmy = (value) =>
  value ? String(value).slice(0, 10).split("-").reverse().join("-") : "-";

/** Value for a datetime-local input, in local time rather than UTC. */
const nowForInput = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const CenterFee = () => {
  const dispatch = useDispatch();

  const [full, setFull] = useState("");
  const [partial, setPartial] = useState("");
  const [renewal, setRenewal] = useState("");
  const [channels, setChannels] = useState({});
  const [saving, setSaving] = useState(false);

  const [list, setList] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  // Add / view payments for one center
  const [payFor, setPayFor] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({});
  const [posting, setPosting] = useState(false);

  const loadList = useCallback(
    async (over = {}) => {
      setLoading(true);
      const data = await dispatch(
        getCenterFeeList({
          search,
          filter,
          page: 1,
          per_page: 10,
          ...over,
        })
      );
      setList(data || false);
      setLoading(false);
    },
    [dispatch, search, filter]
  );

  useEffect(() => {
    (async () => {
      const res = await dispatch(getCenterFeeSetting());
      if (res && res.setting) {
        setFull(String(Number(res.setting.full_amount || 0)));
        setPartial(String(Number(res.setting.partial_amount || 0)));
        setRenewal(String(Number(res.setting.renewal_amount || 0)));
        setChannels(res.channels || {});
      }
      loadList();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleSaveSetting = async (e) => {
    e.preventDefault();
    if (Number(partial) > Number(full)) {
      errorAlert("The partial amount cannot be more than the full amount.");
      return;
    }
    setSaving(true);
    const body = new FormData();
    body.append("full_amount", full);
    body.append("partial_amount", partial);
    body.append("renewal_amount", renewal || 0);
    const saved = await dispatch(saveCenterFeeSetting(body));
    setSaving(false);
    if (saved) loadList();
  };

  const openCenter = async (center, feeType) => {
    setPayFor(center);
    setDetail(null);
    setForm({
      fee_type: feeType,
      amount: String(
        feeType === "renewal" ? center.renewal_balance : center.balance
      ),
      channel: "cash",
      paid_at: nowForInput(),
      trx_id: "",
      receipt_num: "",
      remark: "",
    });
    const d = await dispatch(getCenterFeeDetails(center.id));
    setDetail(d || false);
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPosting(true);
    const body = new FormData();
    body.append("center_id", payFor.id);
    Object.entries(form).forEach(([k, v]) => body.append(k, v));
    const done = await dispatch(addOfflineCenterFee(body));
    setPosting(false);
    if (done) {
      setDetail(done);
      setField("amount", "");
      setField("trx_id", "");
      setField("receipt_num", "");
      setField("remark", "");
      loadList({ page: list?.pagination?.page || 1 });
    }
  };

  const handleRenewalDate = async (date) => {
    if (!date) return;
    const done = await dispatch(setCenterRenewalDate(payFor.id, date));
    if (done) {
      setDetail(done);
      setPayFor({ ...payFor, renewal_date: date });
      loadList({ page: list?.pagination?.page || 1 });
    }
  };

  const handleDelete = async (payment) => {
    if (!window.confirm("Remove this payment? The balance will go back up.")) {
      return;
    }
    const done = await dispatch(deleteCenterFeePayment(payment.id));
    if (done) {
      setDetail(done);
      loadList({ page: list?.pagination?.page || 1 });
    }
  };

  const firstSr = list?.pagination?.first_sr || 1;

  return (
    <div>
      <div class="BigTitle">Center Fee</div>

      {/* ---- the amounts ---- */}
      <form class="card p-3 mt-3" onSubmit={handleSaveSetting}>
        <div class="row align-items-end">
          <div class="col-md-3 mb-2">
            <label>Full Payment Amount</label>
            <input
              type="number"
              min="0"
              step="1"
              class="form-control"
              value={full}
              onChange={(e) => setFull(e.target.value)}
              required
            />
          </div>
          <div class="col-md-3 mb-2">
            <label>Partial Payment Amount</label>
            <input
              type="number"
              min="0"
              step="1"
              class="form-control"
              value={partial}
              onChange={(e) => setPartial(e.target.value)}
              required
            />
            <small class="text-muted">Smallest first instalment allowed.</small>
          </div>
          <div class="col-md-3 mb-2">
            <label>Yearly Renewal Fee</label>
            <input
              type="number"
              min="0"
              step="1"
              class="form-control"
              value={renewal}
              onChange={(e) => setRenewal(e.target.value)}
            />
            <small class="text-muted">Leave 0 if centers do not renew.</small>
          </div>
          <div class="col-md-3 mb-2">
            <button class="btn btn-Next" type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save Fee"}
            </button>
          </div>
        </div>
        {Number(full) > 0 ? (
          <small class="text-muted mt-2">
            Centers will be asked for {rupees(full)} in full, or at least{" "}
            {rupees(partial)} as a first payment
            {Number(renewal) > 0
              ? `, then ${rupees(renewal)} every year to renew.`
              : "."}
          </small>
        ) : (
          <small class="text-danger mt-2">
            While the full amount is 0 no center can pay - their fee page tells
            them it is not set yet.
          </small>
        )}
      </form>

      {/* ---- totals ---- */}
      {list && (
        <div class="row mt-4">
          <div class="col-md-4">
            <div class="card p-3 mb-2">
              <small class="text-muted">Centers</small>
              <h4 class="mb-0">{list.pagination.total}</h4>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card p-3 mb-2">
              <small class="text-muted">Collected</small>
              <h4 class="mb-0 text-success">{rupees(list.total_paid)}</h4>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card p-3 mb-2">
              <small class="text-muted">Outstanding</small>
              <h4 class="mb-0 text-danger">{rupees(list.total_due)}</h4>
            </div>
          </div>
        </div>
      )}

      {/* ---- search and filter ---- */}
      <div class="row mt-3 mb-2">
        <div class="col-md-5 mb-2">
          <input
            type="text"
            class="form-control"
            placeholder="Search by center name, code or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") loadList({ page: 1 });
            }}
          />
        </div>
        <div class="col-md-4 mb-2">
          <select
            class="form-control"
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              loadList({ filter: e.target.value, page: 1 });
            }}
          >
            <option value="all">All centers</option>
            <option value="unpaid">Not paid</option>
            <option value="partial">Part paid</option>
            <option value="paid">Fully paid</option>
            <option value="renewal_due">Renewal due</option>
          </select>
        </div>
        <div class="col-md-3 mb-2">
          <button
            class="btn btn-comman"
            type="button"
            onClick={() => loadList({ page: 1 })}
          >
            Search
          </button>
        </div>
      </div>

      {list && (
        <Paging
          pagination={list.pagination}
          onPage={(p) => loadList({ page: p })}
        />
      )}

      {/* ---- centers ---- */}
      <div class="table-responsive">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Sr. No.</th>
              <th>Center</th>
              <th>Code</th>
              <th>Fee</th>
              <th>Paid</th>
              <th>Balance</th>
              <th>Status</th>
              <th>Renewal Date</th>
              <th>Renewal Due</th>
              <th>Add Fee</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="10">Loading...</td>
              </tr>
            )}
            {!loading && list && list.centers.length === 0 && (
              <tr>
                <td colSpan="10">No centers match this search.</td>
              </tr>
            )}
            {!loading &&
              list &&
              list.centers.map((c, i) => (
                <tr key={c.id}>
                  <td>{firstSr + i}</td>
                  <td>
                    {c.center_name}
                    <br />
                    <small class="text-muted">{c.center_state}</small>
                  </td>
                  <td>{c.center_code}</td>
                  <td>{rupees(c.full_amount)}</td>
                  <td>{rupees(c.paid)}</td>
                  <td class={c.balance > 0 ? "text-danger" : ""}>
                    {rupees(c.balance)}
                  </td>
                  <td>{STATE_LABEL[c.fee_state]}</td>
                  <td class={c.renewal_overdue ? "text-danger" : ""}>
                    {dmy(c.renewal_date)}
                    {c.renewal_overdue && (
                      <>
                        <br />
                        <small>overdue</small>
                      </>
                    )}
                  </td>
                  <td>
                    {Number(c.renewal_amount) > 0
                      ? rupees(c.renewal_balance)
                      : "-"}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button
                      class="btn btn-sm btn-comman mb-1"
                      type="button"
                      onClick={() => openCenter(c, "registration")}
                    >
                      Add Fee
                    </button>
                    {Number(c.renewal_amount) > 0 && (
                      <button
                        class="btn btn-sm btn-Next"
                        type="button"
                        onClick={() => openCenter(c, "renewal")}
                      >
                        Renewal
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {list && (
        <Paging
          pagination={list.pagination}
          onPage={(p) => loadList({ page: p })}
        />
      )}

      {/* ---- add a payment / see this center's history ---- */}
      <Modal show={!!payFor} onHide={() => setPayFor(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {form.fee_type === "renewal" ? "Renewal Fee" : "Registration Fee"}
            {payFor ? ` - ${payFor.center_name}` : ""}
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            {payFor && (
              <>
                <p class="mb-3">
                  <strong>{payFor.center_code}</strong> &nbsp;|&nbsp; Balance
                  due:{" "}
                  {rupees(
                    form.fee_type === "renewal"
                      ? payFor.renewal_balance
                      : payFor.balance
                  )}
                </p>

                <div class="row">
                  <div class="col-md-4 mb-3">
                    <label>Amount Received</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      class="form-control"
                      value={form.amount}
                      onChange={(e) => setField("amount", e.target.value)}
                      required
                    />
                  </div>
                  <div class="col-md-4 mb-3">
                    <label>Date &amp; Time Received</label>
                    <input
                      type="datetime-local"
                      class="form-control"
                      value={form.paid_at}
                      onChange={(e) => setField("paid_at", e.target.value)}
                      required
                    />
                  </div>
                  <div class="col-md-4 mb-3">
                    <label>Payment Mode</label>
                    <select
                      class="form-control"
                      value={form.channel}
                      onChange={(e) => setField("channel", e.target.value)}
                    >
                      {Object.entries(channels).map(([key, label]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label>
                      Transaction ID
                      {form.channel === "cash" ? " (not needed for cash)" : ""}
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      value={form.trx_id}
                      onChange={(e) => setField("trx_id", e.target.value)}
                      disabled={form.channel === "cash"}
                      placeholder="UPI / gateway reference"
                    />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label>Receipt Number</label>
                    <input
                      type="text"
                      class="form-control"
                      value={form.receipt_num}
                      onChange={(e) => setField("receipt_num", e.target.value)}
                    />
                  </div>
                </div>

                <div class="mb-3">
                  <label>Remark</label>
                  <input
                    type="text"
                    class="form-control"
                    placeholder="Cheque number, who handed it over, anything useful"
                    value={form.remark}
                    onChange={(e) => setField("remark", e.target.value)}
                  />
                </div>

                <small class="text-muted">
                  Use this for money received outside the website. Payments made
                  by the center through the gateway record themselves.
                </small>

                {/*
                  Centers that existed before renewals were introduced have no
                  stored renewal date, so the list guesses one year after they
                  were created - which reads as long overdue. This is where that
                  is corrected.
                */}
                {detail && detail.renewal && detail.renewal.is_set && (
                  <div class="row mt-3 align-items-end">
                    <div class="col-md-6">
                      <label>Next Renewal Due On</label>
                      <input
                        type="date"
                        class="form-control"
                        value={(detail.renewal.due_date || "").slice(0, 10)}
                        onChange={(e) => handleRenewalDate(e.target.value)}
                      />
                      <small class="text-muted">
                        Changing this moves when the center is asked to renew.
                      </small>
                    </div>
                  </div>
                )}

                {/* history for this center */}
                <div class="BigTitle mt-4">Payments so far</div>
                {detail === null && <p>Loading...</p>}
                {detail && (
                  <div class="table-responsive">
                    <table class="table table-bordered">
                      <thead>
                        <tr>
                          <th>Sr.</th>
                          <th>Date</th>
                          <th>For</th>
                          <th>Amount</th>
                          <th>Mode</th>
                          <th>Transaction ID</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.payments.length === 0 && (
                          <tr>
                            <td colSpan="7">No payments yet.</td>
                          </tr>
                        )}
                        {detail.payments.map((p, i) => (
                          <tr key={p.id}>
                            <td>{i + 1}</td>
                            <td>{dmy(p.paid_at || p.created_at)}</td>
                            <td class="text-capitalize">{p.fee_type}</td>
                            <td>{rupees(p.amount)}</td>
                            <td>{p.channel_label}</td>
                            <td>{p.trx_id || "-"}</td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              <a
                                class="btn btn-sm btn-comman mr-1"
                                href={`${FILE_BASE}center-fee-slip/${p.id}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Slip
                              </a>
                              <button
                                class="btn btn-sm btn-danger"
                                type="button"
                                onClick={() => handleDelete(p)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              class="btn btn-comman"
              type="button"
              onClick={() => setPayFor(null)}
            >
              Close
            </button>
            <button class="btn btn-Next" type="submit" disabled={posting}>
              {posting ? "Saving..." : "Save Payment"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default CenterFee;
